import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Checkbox, CircularProgress, TextField, InputAdornment, Button } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    ArrowLeft, ArrowRight, ArrowDownWideNarrow, ArrowUpNarrowWide, Trash2, Search,
    Users, UsersRound, ShieldCheck, AlertTriangle, RefreshCw, Link2, X, ExternalLink,
    CalendarDays, CalendarRange, ChevronDown, Check,
} from 'lucide-react';
import Calling from '../../api/Calling';
import formatDate from '../../components/formatDate';
import getISTDate from '../../components/getISTDate';
import User_Navbar from './User_Navbar';
import MainButton from '../../components/ul/MainButton';
import DeleteButton from '../../components/ul/DeleteButton';
import TableSkeleton from '../../components/ul/TableSkeleton';

// Brand palette
const BRAND = '#34C759';
const BRAND_HOVER = '#2db84d';
const BRAND_SOFT = 'rgba(52,199,89,0.10)';
const BRAND_RING = 'rgba(52,199,89,0.45)';

// Premium green-accent outlined field styling for the date pickers
const dateFieldSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { fontSize: '0.8rem', fontWeight: 500, color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#111827',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${BRAND_SOFT}` },
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#9ca3af',
        borderRadius: '8px',
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': { color: BRAND, backgroundColor: BRAND_SOFT },
    },
};
const applyBtnSx = {
    backgroundColor: BRAND,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 6px 16px -6px rgba(52,199,89,0.6)',
    whiteSpace: 'nowrap',
    px: 2.75,
    height: 40,
    '&:hover': { backgroundColor: BRAND_HOVER, boxShadow: '0 8px 20px -6px rgba(52,199,89,0.7)' },
};

// Premium, fully-branded theme for the calendar popup (keys verified for @mui/x-date-pickers@9.6).
const pickerTheme = createTheme({
    palette: { primary: { main: BRAND } },
    shape: { borderRadius: 12 },
    typography: { fontWeightMedium: 600 },
    components: {
        MuiPickerPopper: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    marginTop: 6,
                    border: '1px solid #eef0f2',
                    boxShadow: '0 18px 50px -12px rgba(16,24,40,0.22), 0 8px 16px -8px rgba(16,24,40,0.10)',
                    overflow: 'hidden',
                },
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: { paddingLeft: 18, paddingRight: 14, marginTop: 12, marginBottom: 4 },
                label: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
                switchViewButton: { color: '#374151', '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND } },
            },
        },
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: { color: '#6b7280', borderRadius: 10, '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND } },
            },
        },
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: { fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase' },
            },
        },
        MuiPickerDay: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 10,
                    transition: 'background-color .12s ease, color .12s ease',
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&:focus': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        boxShadow: '0 4px 10px -3px rgba(52,199,89,0.6)',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
                today: {
                    border: `1.5px solid ${BRAND}`,
                    color: BRAND,
                    fontWeight: 700,
                    '&:not(.Mui-selected)': { backgroundColor: BRAND_SOFT },
                },
            },
        },
        MuiYearCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': { backgroundColor: BRAND, color: '#fff', '&:hover': { backgroundColor: BRAND_HOVER }, '&:focus': { backgroundColor: BRAND_HOVER } },
                },
            },
        },
        MuiMonthCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': { backgroundColor: BRAND, color: '#fff', '&:hover': { backgroundColor: BRAND_HOVER }, '&:focus': { backgroundColor: BRAND_HOVER } },
                },
            },
        },
    },
});

const pickerSlots = {
    openPickerIcon: () => <CalendarDays size={18} />,
    leftArrowIcon: () => <ArrowLeft size={18} />,
    rightArrowIcon: () => <ArrowRight size={18} />,
    switchViewIcon: () => <ChevronDown size={18} />,
};

const QUICK_RANGES = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'last6Months', label: 'Last 6 Months' },
    { key: 'thisYear', label: 'This Year' },
];
const checkboxSx = {
    color: '#9ca3af',
    p: 0.5,
    '&.Mui-checked': { color: BRAND },
    '&.MuiCheckbox-indeterminate': { color: BRAND },
};

const Referral = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [limit] = useState(10);
    const [page, setPage] = useState(1);
    const [totalPage, setTotalPage] = useState(0);
    const [totalRecord, setTotalRecord] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [search, setSearch] = useState('');
    const [ids, setIds] = useState([]);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    // Draft (uncommitted) date-range values for the pickers — applied only on "Apply" click
    const [draftStart, setDraftStart] = useState(dayjs(getISTDate()));
    const [draftEnd, setDraftEnd] = useState(dayjs(getISTDate()));
    const [selectState, setSelectState] = useState('today');
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [sortOrder, setSortOrder] = useState(-1);
    const modalRef = useRef(null);

    const getReferralData = async (showRefresh = false) => {
        if (showRefresh) setIsRefreshing(true);
        else setIsLoading(true);

        const response = await Calling.getReferral(page, limit, startDate, endDate, '', sortOrder, search);
        if (response.status) {
            setData(response.data);
            setTotalPage(response.totalPage);
            setTotalRecord(response.totalRecord || 0);
            setHasNextPage(response.nextPage);
        } else {
            toast.error(response.message);
        }

        setIsLoading(false);
        setIsRefreshing(false);
    }

    useEffect(() => {
        getReferralData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit, startDate, endDate, sortOrder]);

    useEffect(() => {
        if (search.trim() !== '') {
            const delayDebounce = setTimeout(() => {
                getReferralData();
            }, 500);
            return () => clearTimeout(delayDebounce);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // Keep the picker drafts in sync whenever the committed range changes (quick buttons / apply)
    useEffect(() => {
        setDraftStart(startDate ? dayjs(startDate) : null);
        setDraftEnd(endDate ? dayjs(endDate) : null);
    }, [startDate, endDate]);

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowDetailModal(false);
                setSelectedReferral(null);
            }
        };

        if (showDetailModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDetailModal]);

    // Close modal on ESC key
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                setShowDetailModal(false);
                setSelectedReferral(null);
            }
        };

        if (showDetailModal) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [showDetailModal]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
                setSelectState('range');
        }

        setStartDate(start);
        setEndDate(end);
        // keep picker drafts aligned with the quick-range commit so the Apply button doesn't flash
        setDraftStart(start ? dayjs(start) : null);
        setDraftEnd(end ? dayjs(end) : null);
        setPage(1);
    };

    // Commit the picked draft range — this is the ONLY thing that applies a custom range
    const handleApplyDateRange = () => {
        if (!draftStart || !draftStart.isValid() || !draftEnd || !draftEnd.isValid()) {
            toast.error('Please select both start and end dates', { autoClose: 2000 });
            return;
        }
        const today = dayjs().endOf('day');
        if (draftStart.isAfter(today) || draftEnd.isAfter(today)) {
            toast.error('Future dates are not allowed', { autoClose: 2000 });
            return;
        }
        if (draftStart.isAfter(draftEnd, 'day')) {
            toast.error('Start date cannot be after end date', { autoClose: 2000 });
            return;
        }
        setStartDate(draftStart.format('YYYY-MM-DD'));
        setEndDate(draftEnd.format('YYYY-MM-DD'));
        setSelectState('range');
        setPage(1);
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(1);
    }

    const handleCheck = (id) => {
        if (ids.includes(id)) {
            setIds(ids.filter(item => item !== id));
        } else {
            setIds([...ids, id]);
        }
    }

    const handleAllCheck = () => {
        if (ids.length === data.length) {
            setIds([]);
        } else {
            setIds(data.map(item => item._id));
        }
    }

    const handleDeleteClick = () => {
        if (ids.length > 0) {
            setShowDeleteModal(true);
        }
    }

    const handleDelete = async () => {
        setIsDelete(true);
        const response = await Calling.deleteReferral(ids);
        if (response.status) {
            toast.success(response.message);
            getReferralData();
            setIds([]);
        } else {
            toast.error(response.message);
        }
        setIsDelete(false);
        setShowDeleteModal(false);
    }

    const handleUserClick = (id) => {
        if (id) {
            window.open(`/manage/user/all/info/${id}`, '_blank');
        }
    }

    const handleRefresh = () => {
        getReferralData(true);
    }

    const handleRowClick = (item) => {
        setSelectedReferral(item);
        setShowDetailModal(true);
    }

    const handleReportSort = () => {
        setSelectState('sort');
        setSortOrder(sortOrder === 1 ? -1 : 1);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full min-h-screen text-main-font"
        >
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                        <Users size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">User Management</h1>
                        <p className="text-sm text-main-subfont">Manage and monitor all users</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Navigation */}
                <User_Navbar selectState={'referral'} />
                <hr className="border-main-border" />

                <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="p-4 bg-main-exBlue/5 rounded-xl border border-main-exBlue/20">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-main-subfont">Total Referrals</p>
                                    <p className="text-2xl font-bold text-main-font mt-1">
                                        {isLoading ? (
                                            <span className="flex items-center text-main-subfont text-base">
                                                <CircularProgress size={18} sx={{ color: 'inherit', mr: 1 }} />
                                                Loading...
                                            </span>
                                        ) : (
                                            totalRecord.toLocaleString()
                                        )}
                                    </p>
                                </div>
                                <div className="p-3 rounded-xl bg-main-exBlue/10">
                                    <Users size={24} className="text-main-exBlue" />
                                </div>
                            </div>
                        </div>

                        <div className="p-4 bg-main-red/5 rounded-xl border border-main-red/20">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-main-subfont">Current Page</p>
                                    <p className="text-2xl font-bold text-main-font mt-1">{data.length}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-main-red/10">
                                    <UsersRound size={24} className="text-main-red" />
                                </div>
                            </div>
                        </div>

                        <div className="p-4 bg-main-yellow/5 rounded-xl border border-main-yellow/20">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-main-subfont">Total Pages</p>
                                    <p className="text-2xl font-bold text-main-font mt-1">{totalPage}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-main-yellow/10">
                                    <ShieldCheck size={24} className="text-main-yellow" />
                                </div>
                            </div>
                        </div>


                    </div>
                </div>

                {/* Date Range Filters */}
                <div className="px-4 py-4 border-t border-main-border">
                    <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
                        {/* Quick Range — modern segmented control */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarDays size={13} /> Quick Range
                            </p>
                            <div className="inline-flex flex-wrap items-center gap-1 bg-main-bg border border-main-border rounded-xl p-1">
                                {QUICK_RANGES.map((r) => {
                                    const active = selectState === r.key;
                                    return (
                                        <button
                                            key={r.key}
                                            type="button"
                                            onClick={() => handleDateRangeFilter(r.key)}
                                            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                                ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                                : 'text-main-subfont hover:text-main-font hover:bg-main-white'
                                                }`}
                                        >
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Range — themed MUI date pickers + Apply (applies only on click) */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarRange size={13} /> Custom Range
                            </p>
                            <ThemeProvider theme={pickerTheme}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 bg-main-bg border border-main-border rounded-2xl p-2.5 shadow-sm">
                                        <DatePicker
                                            label="From"
                                            value={draftStart}
                                            onChange={(v) => setDraftStart(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            maxDate={draftEnd || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <span className="hidden sm:flex items-center justify-center text-main-subfont">
                                            <ArrowRight size={16} />
                                        </span>
                                        <DatePicker
                                            label="To"
                                            value={draftEnd}
                                            onChange={(v) => setDraftEnd(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            minDate={draftStart || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleApplyDateRange}
                                            sx={{ ...applyBtnSx, display: ((draftStart && draftStart.isValid() ? draftStart.format('YYYY-MM-DD') : '') !== (startDate || '') || (draftEnd && draftEnd.isValid() ? draftEnd.format('YYYY-MM-DD') : '') !== (endDate || '')) ? 'inline-flex' : 'none' }}
                                            startIcon={<Check size={16} />}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                    </div>

                    {/* Applied-range indicator */}
                    {selectState === 'range' && startDate && endDate && (
                        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-main-th border border-main-blue/20 rounded-lg">
                            <CalendarRange size={14} className="text-main-blue" />
                            <span className="text-xs text-main-subfont">
                                Showing <span className="font-semibold text-main-font">{formatDate(startDate)}</span> → <span className="font-semibold text-main-font">{formatDate(endDate)}</span>
                            </span>
                        </div>
                    )}
                </div>

                {/* Action Buttons and Search */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 items-center">
                            <MainButton
                                onClick={handleReportSort}
                                label={
                                    <span className="flex items-center gap-2">
                                        {sortOrder === -1 ? <ArrowDownWideNarrow size={16} /> : <ArrowUpNarrowWide size={16} />}
                                        Sort
                                    </span>
                                }
                                selectState={selectState === 'sort'}
                            />
                            <button
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className="p-2.5 bg-main-bg hover:bg-main-thHover rounded-xl transition-all border border-main-border"
                                title="Refresh"
                            >
                                <RefreshCw className={`text-main-font ${isRefreshing ? 'animate-spin' : ''}`} size={18} />
                            </button>
                            {ids.length > 0 && (
                                <DeleteButton
                                    onClick={handleDeleteClick}
                                    label={
                                        <span className="flex items-center gap-2">
                                            <Trash2 size={16} /> Delete ({ids.length})
                                        </span>
                                    }
                                />
                            )}
                        </div>

                        {/* Search Input */}
                        <div className="w-full lg:w-80">
                            <TextField
                                fullWidth
                                size="small"
                                type="search"
                                value={search}
                                onChange={handleSearch}
                                placeholder="Search by Code or User ID..."
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' } }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search size={18} className="text-main-subfont" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-main-th">
                                <th className="ps-4 py-4 text-left">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            size="small"
                                            sx={checkboxSx}
                                            checked={ids.length === data.length && data.length > 0}
                                            onChange={handleAllCheck}
                                        />
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Referrer</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Code</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Date</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>

                        {isLoading ? (
                            <tbody>
                                <tr>
                                    <td colSpan={7} className="p-0">
                                        <TableSkeleton rows={8} cols={7} />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {data.length > 0 ? data.map((item, index) => (
                                    <tr key={item._id} className="hover:bg-main-thHover transition-colors">
                                        <td className="ps-4 py-4">
                                            <Checkbox
                                                size="small"
                                                sx={checkboxSx}
                                                checked={ids.includes(item._id)}
                                                onChange={() => handleCheck(item._id)}
                                            />
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont">{(page - 1) * limit + index + 1}</td>
                                        <td className="px-4 py-4">
                                            <div
                                                className="flex items-center gap-3 cursor-pointer group"
                                                onClick={() => handleUserClick(item?.u_info?._id)}
                                            >
                                                {item?.u_info?.u_profile ? (
                                                    <img
                                                        src={item.u_info.u_profile}
                                                        alt=""
                                                        className="w-10 h-10 rounded-full object-cover border-2 border-main-border group-hover:border-main-blue transition-colors"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-main-blue text-main-white flex items-center justify-center font-semibold text-sm">
                                                        {item?.u_info?.u_name?.charAt(0).toUpperCase() || 'U'}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-main-font group-hover:text-main-blue transition-colors">
                                                        {item?.u_info?.u_name || 'N/A'}
                                                    </p>
                                                    <p className="text-xs text-main-subfont truncate max-w-[120px]">
                                                        {item?.u_info?.u_email || item?.u_info?.u_device || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div
                                                className="flex items-center gap-3 cursor-pointer group"
                                                onClick={() => handleUserClick(item?.r_info?._id)}
                                            >
                                                {item?.r_info?.u_profile ? (
                                                    <img
                                                        src={item.r_info.u_profile}
                                                        alt=""
                                                        className="w-10 h-10 rounded-full object-cover border-2 border-main-border group-hover:border-main-blue transition-colors"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-main-blue text-main-white flex items-center justify-center font-semibold text-sm">
                                                        {item?.r_info?.u_name?.charAt(0).toUpperCase() || 'R'}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-main-font group-hover:text-green-600 transition-colors">
                                                        {item?.r_info?.u_name || 'N/A'}
                                                    </p>
                                                    <p className="text-xs text-main-subfont truncate max-w-[120px]">
                                                        {item?.r_info?.u_email || item?.r_info?.u_device || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg bg-main-blue text-main-white">
                                                {item.r_code}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont">{formatDate(item.createdAt)}</td>
                                        <td className="px-4 py-4">
                                            <button
                                                onClick={() => handleRowClick(item)}
                                                className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-main-blue text-main-white hover:bg-main-blue/90 transition-colors"
                                                title="View Details"
                                            >
                                                <ExternalLink size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={7} className="py-12">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="p-4 bg-main-bg rounded-full">
                                                    <Users className="text-main-subfont" size={36} />
                                                </div>
                                                <p className="text-main-subfont font-medium">No Referrals Found</p>
                                                <p className="text-sm text-main-subfont">Try adjusting your search criteria</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-6 border-t border-main-border">
                    <div className="flex flex-col md:flex-row justify-center items-center relative">
                        <div className="flex gap-3 mb-4 md:mb-0">
                            <button
                                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                disabled={page === 1}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${page === 1
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                <ArrowLeft size={12} /> Previous
                            </button>
                            <button
                                onClick={() => setPage((prev) => Math.min(prev + 1, totalPage))}
                                disabled={!hasNextPage}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${!hasNextPage
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                Next <ArrowRight size={12} />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-0 text-sm">
                            Page{' '}
                            <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                {page}
                            </span>
                            {' '}of{' '}
                            <span className="font-medium text-main-font">{totalPage}</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <motion.div
                        key="referral-delete-modal"
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-main-white rounded-2xl shadow-2xl max-w-md w-full p-6"
                            initial={{ scale: 0.94, y: 16, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.94, y: 16, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
                                <AlertTriangle className="text-red-500" size={30} />
                            </div>

                            <h3 className="text-xl font-bold text-main-font text-center mb-2">
                                Confirm Deletion
                            </h3>

                            <p className="text-main-subfont text-center mb-6">
                                Are you sure you want to delete <span className="font-semibold text-red-500">{ids.length}</span> referral{ids.length > 1 ? 's' : ''}? This action cannot be undone.
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={isDelete}
                                    className="flex-1 px-4 py-3 bg-main-bg border border-main-border text-main-font rounded-xl font-medium hover:bg-main-thHover transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={isDelete}
                                    className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                                >
                                    {isDelete ? (
                                        <>
                                            <CircularProgress size={16} sx={{ color: '#fff' }} />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={16} /> Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Detail Modal */}
            <AnimatePresence>
                {showDetailModal && selectedReferral && (
                    <motion.div
                        key="referral-detail-modal"
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            ref={modalRef}
                            className="bg-main-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden"
                            initial={{ scale: 0.95, y: 14, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.95, y: 14, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* Modal Header */}
                            <div className="bg-main-blue p-5 text-main-white relative">
                                <button
                                    onClick={() => { setShowDetailModal(false); setSelectedReferral(null); }}
                                    className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                                >
                                    <X size={16} />
                                </button>
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-white/20 rounded-xl">
                                        <Link2 size={24} />
                                    </div>
                                    <div>
                                        <p className="text-white/80 text-sm">Referral Details</p>
                                        <h2 className="text-xl font-bold">{selectedReferral?.r_code || 'N/A'}</h2>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-5 space-y-4 max-h-[50vh] overflow-y-auto">
                                {/* User Card */}
                                <div
                                    onClick={() => handleUserClick(selectedReferral?.u_info?._id)}
                                    className="bg-main-th border border-main-border rounded-xl p-4 cursor-pointer hover:border-main-blue transition-all group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {selectedReferral?.u_info?.u_profile ? (
                                                <img
                                                    src={selectedReferral.u_info.u_profile}
                                                    alt=""
                                                    className="w-12 h-12 rounded-full object-cover border-2 border-main-border"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full bg-main-blue text-main-white flex items-center justify-center font-bold text-lg">
                                                    {selectedReferral?.u_info?.u_name?.charAt(0).toUpperCase() || 'U'}
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-xs text-main-blue font-medium uppercase tracking-wide">User</p>
                                                <p className="font-semibold text-main-font group-hover:text-main-blue transition-colors">
                                                    {selectedReferral?.u_info?.u_name || 'N/A'}
                                                </p>
                                                <p className="text-xs text-main-subfont mt-0.5">{selectedReferral?.u_info?.u_email || selectedReferral?.u_info?.u_device || 'N/A'}</p>
                                            </div>
                                        </div>
                                        <ExternalLink className="text-main-blue group-hover:translate-x-1 transition-transform" size={14} />
                                    </div>
                                </div>

                                {/* Referrer Card */}
                                <div
                                    onClick={() => handleUserClick(selectedReferral?.r_info?._id)}
                                    className="bg-green-50 border border-green-200 rounded-xl p-4 cursor-pointer hover:border-main-blue transition-all group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {selectedReferral?.r_info?.u_profile ? (
                                                <img
                                                    src={selectedReferral.r_info.u_profile}
                                                    alt=""
                                                    className="w-12 h-12 rounded-full object-cover border-2 border-green-300"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full bg-main-blue text-main-white flex items-center justify-center font-bold text-lg">
                                                    {selectedReferral?.r_info?.u_name?.charAt(0).toUpperCase() || 'R'}
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Referrer</p>
                                                <p className="font-semibold text-main-font group-hover:text-green-600 transition-colors">
                                                    {selectedReferral?.r_info?.u_name || 'N/A'}
                                                </p>
                                                <p className="text-xs text-main-subfont mt-0.5">{selectedReferral?.r_info?.u_email || selectedReferral?.r_info?.u_device || 'N/A'}</p>
                                            </div>
                                        </div>
                                        <ExternalLink className="text-green-600 group-hover:translate-x-1 transition-transform" size={14} />
                                    </div>
                                </div>

                                {/* Info Grid */}
                                <div className="bg-main-th border border-main-border rounded-xl p-4">
                                    <p className="text-xs text-main-subfont font-medium uppercase tracking-wide mb-2">Created Date</p>
                                    <p className="text-sm font-semibold text-main-font">{formatDate(selectedReferral?.createdAt)}</p>
                                </div>

                                {/* Referral Code */}
                                <div className="bg-main-th border border-main-blue/20 rounded-xl p-4">
                                    <p className="text-xs text-main-blue font-medium uppercase tracking-wide mb-2">Referral Code</p>
                                    <div className="bg-main-blue text-main-white px-4 py-2 rounded-lg text-center font-mono font-bold text-lg">
                                        {selectedReferral?.r_code}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-4 bg-main-bg border-t border-main-border">
                                <button
                                    onClick={() => { setShowDetailModal(false); setSelectedReferral(null); }}
                                    className="w-full px-4 py-2.5 bg-main-blue text-main-white rounded-xl hover:bg-main-blue/90 transition-colors font-medium"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default Referral;
